import camelcaseKeys from 'camelcase-keys';
import { z } from 'zod';

import {
  LocationIdSchema,
  LRT_POSITION_RANGE,
  LRT_RUN_STATUS,
  REPORT_SCHEDULE,
  LRT_SEARCH_ENGINE,
  type LrtRunStatus
} from '@brightlocal/shared';

/** Schema & type for the settings used to create an LRT report. */
export const LRTCreateReportSettingsSchema = z.object({
  location_id: LocationIdSchema,
  search_engines: z.array(z.string()),
  schedule: z.string(),
  search_location: z.string(),
  keywords: z.array(z.string()),
  run_time_zone: z.string(),
  country: z.string(),
  website_addresses: z.array(z.string()).optional()
});

export type LRTCreateReportSettings = z.infer<typeof LRTCreateReportSettingsSchema>;

/** Schema & type for the response from the LRT create report API. */
export const LRTCreateReportResponseSchema = z
  .object({
    report_id: z.number()
  })
  .transform((data) => camelcaseKeys(data));

export type LRTCreateReportResponse = z.infer<typeof LRTCreateReportResponseSchema>;

export const LRTRunReportResponseSchema = z.object({
  message: z.string()
});

export type LRTRunReportResponse = z.infer<typeof LRTRunReportResponseSchema>;

/**
 * Schema & type for the response from the LRT get report API: the keywords, the schedule and its run
 * day, whether a run is in progress and when the report was last processed. The contract marks no
 * field as required. The API sends `''` for a report without a schedule and `null` for the run day a
 * schedule doesn't use.
 */
export const LRTReportSchema = z
  .object({
    campaign_id: z.number().optional(),
    keywords: z.array(z.string()).optional(),
    schedule: z.nativeEnum(REPORT_SCHEDULE).or(z.literal('')).optional(),
    run_day_of_month: z.number().int().nullish(),
    run_day_of_week: z.string().nullish(),
    run_time: z.string().optional(),
    is_running: z.boolean().optional(),
    last_processed_at: z.string().optional()
  })
  .transform((data) => camelcaseKeys(data, { deep: true }));

export type LRTReport = z.infer<typeof LRTReportSchema>;

/**
 * The set of search engines we know about today, sourced from `LRT_SEARCH_ENGINE`.
 * The result schema below intentionally accepts any string for the engine key/value so
 * a new BE engine slug (e.g. another Bing variant, DuckDuckGo) does not fail the whole
 * `LRTResultSchema.parse(...)`. Consumers that need engine-specific behavior narrow
 * with this schema or its inferred type.
 */
export const LRTSearchEngineSchema = z.nativeEnum(LRT_SEARCH_ENGINE);

export type LRTSearchEngine = z.infer<typeof LRTSearchEngineSchema>;

/**
 * Schema & type for a single ranking row inside `rankings.by_keyword[].results[engine]`.
 * Modelling only the fields we consume today; unknown fields are stripped by Zod.
 * `search_engine` is kept as a plain string for forward compatibility — see
 * `LRTSearchEngineSchema` above.
 */
const LRTResultEntrySchema = z
  .object({
    id: z.number(),
    rank: z.number(),
    keyword: z.string(),
    search_engine: z.string()
  })
  .transform(({ id, rank, keyword, search_engine: searchEngine }) => ({
    id,
    rank,
    keyword,
    searchEngine
  }));

/** Schema & type for a single keyword entry under `rankings.by_keyword`. */
const LRTResultKeywordSchema = z.object({
  keyword: z.string(),
  results: z.record(z.string(), z.array(LRTResultEntrySchema))
});

/**
 * `rankings.by_keyword` is the only field we read here. Manual transform (not
 * `camelcaseKeys` deep) keeps the hyphenated engine keys (`google-mobile`,
 * `google-places`, …) intact so they match the BE-side identifiers verbatim.
 */
const LRTRankingsSchema = z
  .object({
    by_keyword: z.array(LRTResultKeywordSchema)
  })
  .transform(({ by_keyword: byKeyword }) => ({ byKeyword }));

/** Query params for the LRT get result API. */
export type LRTGetResultParams = { campaignHistoryId?: number } | undefined;

/** Schema & type for the response from the LRT get result API. */
export const LRTResultSchema = z.object({
  rankings: LRTRankingsSchema,
  details: z
    .object({
      last_processed_at: z.string().optional()
    })
    .transform((data) => camelcaseKeys(data, { deep: true })),
  urls: z
    .object({
      interactive_url: z.string().optional()
    })
    .transform((data) => camelcaseKeys(data, { deep: true }))
});

export type LRTResult = z.infer<typeof LRTResultSchema>;

/** How the rankings moved against the previous successful update. */
const LRTRunMovementSchema = z.object({
  keywords_up: z.number(),
  keywords_down: z.number(),
  positions_gained: z.number(),
  positions_lost: z.number()
});

/**
 * Aggregate metrics of a successful update, over every keyword and engine of the report. The average
 * counts a keyword that isn't ranking as the search depth + 1. `movement` is `null` on the first update.
 */
const LRTRunMetricsSchema = z.object({
  average_position: z.number(),
  local_pack_coverage: z.number().nullable(),
  movement: LRTRunMovementSchema.nullable()
});

/** Keywords counted in one position range; every range up to the report's search depth is sent, zero counts included. */
const LRTRunPositionRangeSchema = z.object({
  range: z.nativeEnum(LRT_POSITION_RANGE),
  count: z.number()
});

/**
 * Metrics of one search engine within an update; `search_engine` stays a plain string, see
 * `LRTSearchEngineSchema`. The API sends `null` as the average of an engine with no result rows.
 */
const LRTRunSearchEngineMetricsSchema = z.object({
  search_engine: z.string(),
  label: z.string(),
  average_position: z.number().nullable(),
  positions: z.array(LRTRunPositionRangeSchema)
});

/**
 * One rankings row of an update: a keyword's best result on one engine. `position` and `url` are `null`
 * when it isn't ranking; `previous_position` is `null` when it wasn't ranking in the previous successful
 * update, or there is none.
 */
const LRTRunRankingItemSchema = z.object({
  keyword: z.string(),
  search_engine: z.string(),
  position: z.number().nullable(),
  previous_position: z.number().nullable(),
  url: z.string().nullable(),
  serp_features: z.array(z.string())
});

/**
 * Schema & type for a single item in the LRT history response. The `metrics`, `search_engines` and
 * `rankings` blocks come on successful items only, each when the request names it in `fields`.
 */
const LRTHistoryItemSchema = z
  .object({
    report_history_id: z.number(),
    report_id: z.number().optional(),
    location_id: z.number().optional(),
    history_type: z.string().optional(),
    generation_date: z.string(),
    // Sent only when the request names `statuses`
    status: z.nativeEnum(LRT_RUN_STATUS).optional(),
    metrics: LRTRunMetricsSchema.optional(),
    search_engines: z.array(LRTRunSearchEngineMetricsSchema).optional(),
    rankings: z.array(LRTRunRankingItemSchema).optional()
  })
  .transform((data) => camelcaseKeys(data, { deep: true }));

/** An optional block the LRT get history API embeds in each successful item when `fields` names it. */
export type LRTHistoryField = 'metrics' | 'search_engines' | 'rankings';

/** Query params of the LRT get history API; each one left out takes the API's default. */
export interface LRTGetHistoryParams {
  /** Run outcomes to include; the API returns successful runs only when this is left out. */
  statuses?: LrtRunStatus[];
  /** RFC 3339 timestamp: only runs dated on or after it are returned. */
  dateAfter?: string;
  /** Blocks embedded in each successful item; none when this is left out. */
  fields?: LRTHistoryField[];
}

/** Schema & type for the response from the LRT get history API. */
export const LRTHistorySchema = z
  .object({
    total_count: z.number().optional(),
    items: z.array(LRTHistoryItemSchema)
  })
  .transform((data) => camelcaseKeys(data, { deep: true }));

export type LRTHistory = z.infer<typeof LRTHistorySchema>;
